import { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@artstorefronts/ui';
import { Loader2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getMockImageUrl, getMockUrl } from '../utils';
import { ImageDimensions, MockDimensions } from '../types';

const PUBLIC_BUCKET_URL = import.meta.env.VITE_PUBLIC_BUCKET_URL;

interface MockImageProps {
  mock: any;
  asfId: string;
  imageId: number;
  imageDimensions: ImageDimensions;
  mockDimensions: MockDimensions;
  width?: number;
  height?: number;
  onLoad?: () => void;
  className?: string;
  alt?: string;
}

function getLambdaUrl(
  asfId: string,
  mockUuid: string,
  imageId: number,
  imageDimensions: ImageDimensions,
  mockDimensions: MockDimensions,
  width?: number,
  height?: number
) {
  let original = getMockImageUrl(asfId, mockUuid, imageId, imageDimensions, mockDimensions);

  if (width) {
    original += `?w=${width}`;
  }

  if (height) {
    original += `&h=${height}`;
  }

  return original;
}

export function MockImage({
  mock,
  asfId,
  imageId,
  imageDimensions,
  mockDimensions,
  width,
  height,
  onLoad: parentOnLoad,
  className = '',
  alt,
}: MockImageProps) {
  const { t } = useTranslation('artwork');
  const lambdaUrl = getLambdaUrl(asfId, mock.uuid, imageId, imageDimensions, mockDimensions, width, height);
  const s3Url = getMockUrl(
    asfId,
    mock.uuid,
    imageId,
    PUBLIC_BUCKET_URL,
    imageDimensions,
    mockDimensions,
    width,
    height
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Skip the S3-first attempt entirely when PUBLIC_BUCKET_URL is unset
  // (local dev): there is no public bucket to hit, so go straight to the
  // local lambda at :3033 instead of emitting `https://undefined/...` requests.
  const [src, setSrc] = useState(PUBLIC_BUCKET_URL ? s3Url : lambdaUrl);
  const warmTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const s3WarmAttemptedRef = useRef(false);

  useEffect(() => {
    return () => {
      if (warmTimerRef.current) clearTimeout(warmTimerRef.current);
    };
  }, []);

  const handleLoad = useCallback(() => {
    setLoading(false);
    parentOnLoad?.();

    // If we loaded from Lambda, warm the S3 cache in the background so the
    // next page visit can serve from S3 directly. Use a cache-busting param
    // to avoid the browser serving a cached 403 from the earlier S3 failure.
    // Only attempt this when there is an S3 bucket configured (prod/staging).
    if (PUBLIC_BUCKET_URL && src === lambdaUrl && !s3WarmAttemptedRef.current) {
      s3WarmAttemptedRef.current = true;
      warmTimerRef.current = setTimeout(() => {
        const probe = new Image();
        probe.src = `${s3Url}${s3Url.includes('?') ? '&' : '?'}_cb=${Date.now()}`;
      }, 5000);
    }
  }, [src, lambdaUrl, s3Url, parentOnLoad]);

  const handleError = useCallback(() => {
    if (src !== lambdaUrl) {
      setSrc(lambdaUrl);
      return;
    }
    setLoading(false);
    setError(true);
  }, [src, lambdaUrl]);

  return (
    <div className={`relative ${className}`}>
      {loading && !error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      )}

      <img
        src={src}
        alt={alt || mock?.description || t('mock.preview-alt')}
        className={`w-full h-full object-contain transition-opacity duration-300 ${
          loading || error ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={handleLoad}
        onError={handleError}
      />

      {error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              setError(false);
              setLoading(true);
              setSrc(lambdaUrl);
            }}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
